import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Sun, Moon, Laptop, LogOut, User, Upload, BarChart3, Gift, Settings, Search, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import NotificationBell from './NotificationBell';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const filterRef = useRef(null);

  const CATEGORIES = [
    { value: '', label: 'Toutes catégories', icon: '🎬' },
    { value: 'music', label: 'Musique', icon: '🎵' },
    { value: 'sports', label: 'Sports', icon: '⚽' },
    { value: 'education', label: 'Éducation', icon: '📚' },
    { value: 'entertainment', label: 'Divertissement', icon: '🎭' },
    { value: 'news', label: 'Actualités', icon: '📰' },
    { value: 'documentary', label: 'Documentaire', icon: '🎞️' },
    { value: 'short_film', label: 'Court métrage', icon: '🎥' },
    { value: 'feature_film', label: 'Long métrage', icon: '🎦' },
    { value: 'animation', label: 'Animation', icon: '🎨' },
    { value: 'reality_tv', label: 'Téléréalité', icon: '📺' },
    { value: 'show', label: 'Émission', icon: '🎤' },
    { value: 'comedy', label: 'Comédie', icon: '😂' },
    { value: 'religion', label: 'Religion', icon: '✝️' },
    { value: 'cooking', label: 'Cuisine', icon: '🍳' },
    { value: 'fashion', label: 'Mode', icon: '👗' },
    { value: 'tech', label: 'Technologie', icon: '💻' },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  return (
    <nav className="navbar-shrink fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-sm">
      <div className="container max-w-7xl mx-auto px-4 h-16">
        <div className="flex items-center justify-between h-full gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img
              src="/yetutv-logo.png"
              alt="YetuTv"
              className="w-10 h-10 object-contain"
            />
            <div className="hidden sm:block">
              <div className="text-xl font-bold tracking-tight">YetuTv</div>
            </div>
          </Link>

          {/* Search Bar + Filter - Desktop */}
          {user && (
            <div className="hidden md:flex flex-1 max-w-xl items-center gap-2">
              <form onSubmit={handleSearch} className="flex-1">
                <div className="relative w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher des vidéos..."
                    className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="navbar-search-input"
                  />
                </div>
              </form>
              {/* Category Filter */}
              <div className="relative" ref={filterRef}>
                <button
                  type="button"
                  onClick={() => setFilterOpen(!filterOpen)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-background border border-border rounded-lg text-sm hover:bg-muted transition-colors whitespace-nowrap"
                  title="Filtrer par catégorie"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  <span className="hidden lg:inline">Filtrer</span>
                </button>
                {filterOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg py-1 z-50">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => {
                          navigate(cat.value ? `/?category=${cat.value}` : '/');
                          setFilterOpen(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2 transition-colors"
                      >
                        <span>{cat.icon}</span>
                        <span>{cat.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-4">
            {user && (
              <>
                <Link
                  to="/"
                  className="text-sm font-medium hover:text-primary transition-colors"
                >
                  Accueil
                </Link>
                {(user.role === 'creator' || user.role === 'superadmin') && (
                  <Link
                    to="/creator/studio"
                    className="text-sm font-medium hover:text-primary transition-colors"
                  >
                    Studio Créateur
                  </Link>
                )}
                {user.role === 'superadmin' && (
                  <Link
                    to="/admin/creator-requests"
                    className="text-sm font-medium hover:text-primary transition-colors"
                  >
                    Admin
                  </Link>
                )}
              </>
            )}

            {/* Theme Toggle */}
            <div className="relative">
              <button
                onClick={() => setThemeMenuOpen(!themeMenuOpen)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                data-testid="theme-toggle-button"
              >
                {theme === 'light' && <Sun className="w-5 h-5" />}
                {theme === 'dark' && <Moon className="w-5 h-5" />}
                {theme === 'system' && <Laptop className="w-5 h-5" />}
              </button>
              {themeMenuOpen && (
                <div
                  className="absolute right-0 mt-2 w-40 bg-card border border-border rounded-lg shadow-lg py-1"
                  data-testid="theme-toggle-menu"
                >
                  <button
                    onClick={() => {
                      toggleTheme('light');
                      setThemeMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
                  >
                    <Sun className="w-4 h-4" /> Clair
                  </button>
                  <button
                    onClick={() => {
                      toggleTheme('dark');
                      setThemeMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
                  >
                    <Moon className="w-4 h-4" /> Sombre
                  </button>
                  <button
                    onClick={() => {
                      toggleTheme('system');
                      setThemeMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
                  >
                    <Laptop className="w-4 h-4" /> Système
                  </button>
                </div>
              )}
            </div>

            {user ? (
              <div className="flex items-center gap-3">
                {/* Notifications */}
                <NotificationBell />
                
                {/* Points Counter */}
                <Link
                  to="/referral"
                  className="flex items-center gap-2 px-3 py-1.5 bg-accent/10 hover:bg-accent/20 rounded-lg transition-colors"
                  data-testid="points-counter"
                >
                  <Gift className="w-4 h-4 text-accent" />
                  <span className="text-sm font-semibold text-accent">{user.points || 0}</span>
                </Link>

                {/* Profile Menu */}
                <div className="relative" ref={profileMenuRef}>
                  <button
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="flex items-center gap-2 p-2 hover:bg-muted rounded-lg transition-colors"
                    data-testid="profile-menu-button"
                  >
                    <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </button>

                  {profileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-lg shadow-lg py-2 z-50">
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-border">
                        <p className="font-medium text-foreground">{user.username}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        <Link
                          to="/my-profile"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-accent transition-colors"
                        >
                          <User className="w-4 h-4" />
                          Mon Profil
                        </Link>
                        <Link
                          to="/settings"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-accent transition-colors"
                        >
                          <Settings className="w-4 h-4" />
                          Paramètres
                        </Link>
                        {user.role === 'viewer' && (
                          <Link
                            to="/become-creator"
                            onClick={() => setProfileMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-accent transition-colors text-primary"
                          >
                            <Upload className="w-4 h-4" />
                            Devenir Créateur
                          </Link>
                        )}
                      </div>

                      {/* Logout */}
                      <div className="border-t border-border pt-1">
                        <button
                          onClick={() => {
                            setProfileMenuOpen(false);
                            handleLogout();
                          }}
                          className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-accent transition-colors text-destructive w-full"
                        >
                          <LogOut className="w-4 h-4" />
                          Déconnexion
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="text-sm font-medium hover:text-primary transition-colors"
                >
                  Connexion
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  S'inscrire
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 hover:bg-muted rounded-lg"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-border">
          <div className="container max-w-7xl mx-auto px-4 py-4 space-y-3">
            {/* Mobile Search */}
            {user && (
              <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-sm"
                  />
                </div>
              </form>
            )}

            {user ? (
              <>
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-2 hover:bg-muted rounded-lg"
                >
                  Accueil
                </Link>
                <Link
                  to="/my-profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-2 hover:bg-muted rounded-lg"
                >
                  Mon Profil
                </Link>
                {(user.role === 'creator' || user.role === 'superadmin') && (
                  <Link
                    to="/creator/studio"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 hover:bg-muted rounded-lg"
                  >
                    Studio Créateur
                  </Link>
                )}
                {user.role === 'superadmin' && (
                  <Link
                    to="/admin/creator-requests"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 hover:bg-muted rounded-lg"
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to="/settings"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-2 hover:bg-muted rounded-lg"
                >
                  Paramètres
                </Link>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                  className="block w-full text-left px-4 py-2 hover:bg-muted rounded-lg text-destructive"
                >
                  Déconnexion
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-2 hover:bg-muted rounded-lg"
                >
                  Connexion
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-2 bg-primary text-primary-foreground rounded-lg text-center"
                >
                  S'inscrire
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
